import React, { useState, useEffect, useCallback } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import ImageDropzone from '../../components/ImageDropzone';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import NotFound from '../../components/UI/NotFound';
import { HiOutlinePlus, HiOutlineRefresh, HiOutlineSearch } from 'react-icons/hi';
import { FaEdit, FaTrashAlt, FaCopy, FaToggleOn, FaImages, FaFolder, FaTimesCircle, FaEye, FaDownload } from 'react-icons/fa';

const PAGE_LIMIT = 10;

// Download the ORIGINAL (full-quality) image — blob first, fall back to opening in a new tab
const downloadImage = async (url, filename) => {
    if (!url) return;
    try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error('network');
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = filename || 'wallpaper.jpg';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
    } catch (e) {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.click();
    }
};

// ---- Centered Edit Modal ----
const EditModal = ({ wallpaper, categories, onClose, onSaved }) => {
    const [name, setName] = useState(wallpaper.w_name || '');
    const [catId, setCatId] = useState(wallpaper.w_category?._id || wallpaper.w_category || '');
    const [status, setStatus] = useState(wallpaper.w_status);
    const [imageFile, setImageFile] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!name?.trim()) { setError('Name is required.'); return; }
        if (!catId) { setError('Category is required.'); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('w_name', name.trim());
            fd.append('w_category', catId);
            fd.append('w_status', status);
            if (imageFile) fd.append('image', imageFile);

            const resp = await Calling.updateWallpaper(wallpaper._id, fd);
            if (resp?.status) {
                toast.success(resp?.message);
                onSaved();
                onClose();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Something went wrong.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-main-blue/10 rounded-lg"><FaEdit className="text-main-blue" /></div>
                        <h2 className="text-xl font-semibold text-gray-800">Edit Wallpaper</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FaTimesCircle size={22} /></button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Image */}
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Image</label>
                        <ImageDropzone existingUrl={wallpaper.w_image} onChange={setImageFile} />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="Wallpaper name" />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
                        <select value={catId} onChange={(e) => setCatId(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                            <option value="">Select category</option>
                            {categories.map((c) => <option key={c._id} value={c._id}>{c.w_name}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
                        <label className="relative inline-flex items-center cursor-pointer mt-2">
                            <input type="checkbox" checked={status} onChange={() => setStatus(!status)} className="sr-only peer" />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                            <span className="ml-3 text-sm font-medium text-gray-700">{status ? 'Active' : 'Inactive'}</span>
                        </label>
                    </div>

                    {error && <p className="text-xs text-red-500">{error}</p>}
                </div>

                <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                    <button onClick={handleSave} disabled={saving}
                        className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 flex items-center gap-2 disabled:opacity-50">
                        <FaEdit className="text-sm" />{saving ? 'Updating...' : 'Update'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---- Centered View Modal ----
const ViewModal = ({ wallpaper, onClose }) => {
    const formatDate = (d) =>
        d ? new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-main-blue/10 rounded-lg"><FaEye className="text-main-blue" /></div>
                        <h2 className="text-xl font-semibold text-gray-800">Wallpaper Details</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FaTimesCircle size={22} /></button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="flex justify-center">
                        <img src={wallpaper.w_image} alt={wallpaper.w_name}
                            className="max-h-72 rounded-lg object-contain border border-gray-200 bg-gray-50"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-3">
                        <div className="flex items-start">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Name</div>
                            <div className="flex-1 text-sm text-gray-900 break-all">{wallpaper.w_name}</div>
                        </div>
                        <div className="flex items-start gap-2">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Slug</div>
                            <div className="flex-1 text-sm text-gray-900 font-mono break-all">{wallpaper.w_slug}</div>
                            <button onClick={() => { navigator.clipboard.writeText(wallpaper.w_slug); toast.success('Slug copied!'); }}
                                className="flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 flex-shrink-0" title="Copy slug">
                                <FaCopy />
                            </button>
                        </div>
                        <div className="flex items-start">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Category</div>
                            <div className="flex-1 text-sm text-gray-900">{wallpaper.w_category?.w_name || '-'}</div>
                        </div>
                        <div className="flex items-start">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Views</div>
                            <div className="flex-1 text-sm text-gray-900">{wallpaper.w_view_count ?? 0}</div>
                        </div>
                        <div className="flex items-start">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Status</div>
                            <div className="flex-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${wallpaper.w_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {wallpaper.w_status ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Created</div>
                            <div className="flex-1 text-sm text-gray-900">{formatDate(wallpaper.createdAt)}</div>
                        </div>
                        <div className="flex items-start">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Updated</div>
                            <div className="flex-1 text-sm text-gray-900">{formatDate(wallpaper.updatedAt)}</div>
                        </div>
                        <div className="flex items-start gap-2">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">ID</div>
                            <div className="flex-1 text-sm text-gray-500 font-mono break-all">{wallpaper._id}</div>
                            <button onClick={() => { navigator.clipboard.writeText(wallpaper._id); toast.success('ID copied!'); }}
                                className="flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 flex-shrink-0" title="Copy ID">
                                <FaCopy />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
                    <button onClick={() => downloadImage(wallpaper.w_image, `${wallpaper.w_slug || 'wallpaper'}.jpg`)}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2">
                        <FaDownload /> Download
                    </button>
                    <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Close</button>
                </div>
            </div>
        </div>
    );
};

const WallpaperList = () => {
    const [loader, setLoader] = useState(false);
    const [wallpapers, setWallpapers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filters / paging
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [order, setOrder] = useState('desc');

    // Add form
    const [name, setName] = useState('');
    const [catId, setCatId] = useState('');
    const [status, setStatus] = useState(true);
    const [imageFile, setImageFile] = useState(null);
    const [uploadKey, setUploadKey] = useState(0);
    const [error, setError] = useState(null);

    const [selected, setSelected] = useState([]);
    const [editWallpaper, setEditWallpaper] = useState(null);
    const [viewWallpaper, setViewWallpaper] = useState(null);

    const fetchCategories = useCallback(async () => {
        const resp = await Calling.getWallpaperCategory({ page: 1, limit: 200, sortBy: 'w_order', order: 'asc' });
        if (resp?.status) setCategories(resp?.data || []);
    }, []);

    const fetchData = useCallback(async () => {
        setLoader(true);
        try {
            const resp = await Calling.getWallpaper({
                page, limit: PAGE_LIMIT, search, category: categoryFilter, status: statusFilter, sortBy, order,
            });
            if (resp?.status) {
                setWallpapers(resp?.data || []);
                setTotalRecords(resp?.totalRecords || 0);
                setTotalPages(resp?.totalPages || 1);
            } else {
                setWallpapers([]);
                setTotalRecords(0);
                setTotalPages(1);
                if (resp?.message) toast.error(resp.message);
            }
        } catch (err) {
            toast.error('Failed to fetch wallpapers');
        } finally {
            setLoader(false);
        }
    }, [page, search, categoryFilter, statusFilter, sortBy, order]);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);
    useEffect(() => { fetchData(); }, [fetchData]);

    const resetForm = () => {
        setName(''); setCatId(''); setStatus(true);
        setImageFile(null); setUploadKey((k) => k + 1); setError(null);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!name?.trim()) return setError('Name is required.');
        if (!catId) return setError('Category is required.');
        if (!imageFile) return setError('Image is required.');
        setLoader(true);
        try {
            const fd = new FormData();
            fd.append('image', imageFile);
            fd.append('w_category', catId);
            fd.append('w_name', name.trim());
            fd.append('w_status', status);

            const resp = await Calling.addWallpaper(fd);
            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
                fetchData();
                fetchCategories();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Something went wrong.');
        } finally {
            setLoader(false);
        }
    };

    const handleToggleStatus = async (w) => {
        setLoader(true);
        try {
            const fd = new FormData();
            fd.append('w_status', !w.w_status);
            const resp = await Calling.updateWallpaper(w._id, fd);
            if (resp?.status) { toast.success(resp?.message); fetchData(); }
            else toast.error(resp?.message);
        } catch (err) {
            toast.error('Failed to update status.');
        } finally {
            setLoader(false);
        }
    };

    const handleDelete = async () => {
        if (selected.length === 0) return toast.error('Please select at least one wallpaper to delete.');
        const check = generateCaptcha('You are about to delete selected wallpapers. Solve this to confirm.');
        if (!check) return toast.error('Incorrect answer.');
        setLoader(true);
        try {
            const resp = await Calling.deleteWallpaper({ id: selected });
            if (resp?.status) {
                toast.success(resp?.message);
                setSelected([]);
                fetchData();
                fetchCategories();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Failed to delete wallpapers.');
        } finally {
            setLoader(false);
        }
    };

    const handleSearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); };
    const toggleSelect = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    const selectAll = (e) => setSelected(e.target.checked ? wallpapers.map((w) => w._id) : []);
    const copyId = (id) => { navigator.clipboard.writeText(id); toast.success('ID copied!'); };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                    <div className="mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Wallpapers</h1>
                                <p className="mt-1 text-sm text-gray-500">Manage wallpapers under each category</p>
                            </div>
                            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white shadow-sm">
                                <HiOutlineRefresh className="text-lg" /> <span>Refresh</span>
                            </button>
                        </div>

                        {/* Add Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-6">
                            <div className="p-5 border-b border-gray-100 flex items-center gap-3">
                                <div className="p-2.5 bg-main-blue/10 rounded-lg"><HiOutlinePlus className="text-main-blue text-xl" /></div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800">Add New Wallpaper</h2>
                                    <p className="text-xs text-gray-500 mt-0.5">Slug is auto-generated from the name</p>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Image */}
                                    <div className="md:col-span-2">
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaImages className="text-main-blue text-sm" /> Image</label>
                                        <ImageDropzone key={uploadKey} onChange={setImageFile} />
                                    </div>

                                    <div>
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaImages className="text-main-blue text-sm" /> Name</label>
                                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="e.g., Sunset Beach" />
                                    </div>

                                    <div>
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaFolder className="text-main-blue text-sm" /> Category</label>
                                        <select value={catId} onChange={(e) => setCatId(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                            <option value="">Select category</option>
                                            {categories.map((c) => <option key={c._id} value={c._id}>{c.w_name}</option>)}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaToggleOn className="text-main-blue text-sm" /> Status</label>
                                        <label className="relative inline-flex items-center cursor-pointer mt-2">
                                            <input type="checkbox" checked={status} onChange={() => setStatus(!status)} className="sr-only peer" />
                                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                            <span className="ml-3 text-sm font-medium text-gray-700">{status ? 'Active' : 'Inactive'}</span>
                                        </label>
                                    </div>

                                    {error && <p className="md:col-span-2 text-xs text-red-500">{error}</p>}

                                    <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
                                        <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">Reset</button>
                                        <button type="submit" className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 flex items-center gap-2">
                                            <HiOutlinePlus className="text-sm" /> Save Wallpaper
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Toolbar */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
                            <div className="flex flex-col md:flex-row md:items-center gap-3">
                                <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                                    <div className="relative flex-1">
                                        <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="Search by name..." />
                                    </div>
                                    <button type="submit" className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80">Search</button>
                                </form>

                                <select value={categoryFilter} onChange={(e) => { setPage(1); setCategoryFilter(e.target.value); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="">All Categories</option>
                                    {categories.map((c) => <option key={c._id} value={c._id}>{c.w_name}</option>)}
                                </select>

                                <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="">All Status</option>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>

                                <select value={`${sortBy}:${order}`} onChange={(e) => { const [s, o] = e.target.value.split(':'); setSortBy(s); setOrder(o); setPage(1); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="createdAt:desc">Newest</option>
                                    <option value="w_name:asc">Name A–Z</option>
                                    <option value="w_view_count:desc">Most Viewed</option>
                                </select>
                            </div>
                        </div>

                        {/* List */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg"><FaImages className="text-main-blue text-xl" /></div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Wallpapers</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            <span className="px-2 py-0.5 bg-main-blue/10 text-main-blue rounded-full text-xs font-medium">Total: {totalRecords}</span>
                                        </p>
                                    </div>
                                </div>
                                {selected.length > 0 && (
                                    <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                                        <FaTrashAlt className="text-sm" /> Delete Selected ({selected.length})
                                    </button>
                                )}
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                {wallpapers.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-4 py-3 text-left">
                                                        <input type="checkbox" className="h-4 w-4 text-main-blue border-gray-300 rounded"
                                                            onChange={selectAll} checked={selected.length === wallpapers.length && wallpapers.length > 0} />
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {wallpapers.map((w) => (
                                                    <tr key={w._id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-4">
                                                            <input type="checkbox" className="h-4 w-4 text-main-blue border-gray-300 rounded"
                                                                onChange={() => toggleSelect(w._id)} checked={selected.includes(w._id)} />
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            <img src={w.w_thumbnail || w.w_image} alt={w.w_name}
                                                                className="w-12 h-12 object-cover rounded-md border border-gray-200 bg-gray-50"
                                                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{w.w_name}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{w.w_category?.w_name || '-'}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{w.w_view_count ?? 0}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input type="checkbox" checked={w.w_status} onChange={() => handleToggleStatus(w)} className="sr-only peer" />
                                                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                                            </label>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                                            <div className="flex items-center gap-2">
                                                                <button onClick={() => setViewWallpaper(w)} className="flex items-center px-2 py-1 bg-main-blue/10 text-main-blue rounded-md hover:bg-main-blue/20" title="View"><FaEye /></button>
                                                                <button onClick={() => setEditWallpaper(w)} className="flex items-center px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100" title="Edit"><FaEdit /></button>
                                                                <button onClick={() => downloadImage(w.w_image, `${w.w_slug || 'wallpaper'}.jpg`)} className="flex items-center px-2 py-1 bg-amber-100 text-amber-600 rounded-md hover:bg-amber-200" title="Download original"><FaDownload /></button>
                                                                <button onClick={() => copyId(w._id)} className="flex items-center px-2 py-1 bg-green-100 text-green-600 rounded-md hover:bg-green-200" title="Copy ID"><FaCopy /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        <div className="flex items-center justify-between mt-4 px-1">
                                            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                                            <div className="flex gap-2">
                                                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                                                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <NotFound />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {editWallpaper && (
                <EditModal
                    wallpaper={editWallpaper}
                    categories={categories}
                    onClose={() => setEditWallpaper(null)}
                    onSaved={() => { fetchData(); fetchCategories(); }}
                />
            )}

            {viewWallpaper && (
                <ViewModal wallpaper={viewWallpaper} onClose={() => setViewWallpaper(null)} />
            )}
        </>
    );
};

export default WallpaperList;
